// mission-blueprint-binding.ts
//
// B3 (plan 20260915-1650, phase-00), bước 0 finding: `InitRequestSchema`
// validates `blueprint.source_path` and `initMission` then DROPPED it — the
// Bug J shape `mission-actor-binding.ts` (actor) and
// `mission-declared-resources.ts` (resource boundary) were each written to
// close. Without the path, nothing at dispatch time can find the file whose
// hash `gates.g0.artifact_hash` was approved against, so editing BLUEPRINT.md
// after approval left the approval standing.
//
// This module owns the persisted declaration: `blueprint-binding.json` in the
// CONTROL root keeps the declaration (path + hash, and whether init verified
// it at base_sha) for the dispatch recheck. Written from the validated request
// — the person's declaration — never from worker output, and out of the
// worker's worktree by construction. The init-time check against the committed
// bytes lives in mission-blueprint-committed-bytes-check.ts.

import { readFile } from "node:fs/promises";
import { isAbsolute, join, posix } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";

const BLUEPRINT_BINDING_FILE_NAME = "blueprint-binding.json";

export interface MissionBlueprintBinding {
  schema_version: 1;
  /** Repo-relative path as declared in the init request (e.g. `.avatar/BLUEPRINT.md`). */
  source_path: string;
  /** Lowercase sha256 hex of the blueprint bytes at init. */
  bytes_sha256: string;
  bound_at: string;
  /**
   * H20: true when init verified these bytes committed at base_sha, so the
   * mission worktree started WITH the file and its later absence means it was
   * removed (dispatch recheck fails closed). False for an empty-repo null-oid
   * mission — nothing was committed to verify — and read as false from a
   * binding written before this field existed.
   */
  verified_at_base_sha: boolean;
}

function bindingPath(controlRoot: string): string {
  return join(controlRoot, BLUEPRINT_BINDING_FILE_NAME);
}

/**
 * `source_path` must stay inside the repo: relative, and not escaping via
 * `..` (also when hidden mid-path, e.g. `docs/../../x`). Returns a problem
 * message, or `undefined` when the path is acceptable.
 */
export function validateBlueprintSourcePath(sourcePath: string): string | undefined {
  const unixy = sourcePath.replace(/\\/g, "/");
  if (unixy.trim() === "" || isAbsolute(unixy) || /^[A-Za-z]:\//.test(unixy)) {
    return `blueprint.source_path "${sourcePath}" phải là đường dẫn TƯƠNG ĐỐI tính từ gốc repo.`;
  }
  const normalised = posix.normalize(unixy);
  if (normalised === "." || normalised === ".." || normalised.startsWith("../")) {
    return `blueprint.source_path "${sourcePath}" trỏ ra ngoài repo — không chấp nhận.`;
  }
  return undefined;
}

/**
 * Where the declared blueprint lives inside a mission worktree. Backslashes
 * are read as separators, as init's `git show` lookup does (review 260930 L2:
 * `docs\BLUEPRINT.md` verified at init must not look "removed" on POSIX).
 */
export function resolveBlueprintPathInWorktree(repoDir: string, sourcePath: string): string {
  return join(repoDir, sourcePath.replace(/\\/g, "/"));
}

export async function writeMissionBlueprintBinding(
  controlRoot: string,
  binding: { source_path: string; bytes_sha256: string; verified_at_base_sha: boolean },
  now: () => Date,
): Promise<void> {
  const payload: MissionBlueprintBinding = {
    schema_version: 1,
    source_path: binding.source_path,
    bytes_sha256: binding.bytes_sha256.toLowerCase(),
    bound_at: now().toISOString(),
    verified_at_base_sha: binding.verified_at_base_sha,
  };
  await writeFileDurable(
    bindingPath(controlRoot),
    Buffer.from(`${JSON.stringify(payload, null, 2)}\n`, "utf8"),
  );
}

/**
 * `undefined` = unknown (missing, corrupt, or wrong shape) — a mission
 * initialised before this phase. Callers skip the recheck rather than guess a
 * path; a hand-edited file with the wrong shape is not coerced.
 */
export async function readMissionBlueprintBinding(
  controlRoot: string,
): Promise<MissionBlueprintBinding | undefined> {
  let raw: string;
  try {
    raw = await readFile(bindingPath(controlRoot), "utf8");
  } catch {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const { source_path, bytes_sha256, bound_at } = parsed;
    if (
      typeof source_path !== "string" ||
      typeof bytes_sha256 !== "string" ||
      typeof bound_at !== "string"
    ) {
      return undefined;
    }
    return {
      schema_version: 1,
      source_path,
      bytes_sha256,
      bound_at,
      verified_at_base_sha: parsed.verified_at_base_sha === true,
    };
  } catch {
    return undefined;
  }
}
